/**
 * Architecture diagram — side-by-side comparison of BLE, Cellular/GNSS, and
 * checkpoint (RFID/NFC/Wi-Fi) shipment tracking architectures.
 */
import React from "react";

export default function ArchitectureDiagram({ title = "Tracking architectures — how the data reaches the platform" }) {
  return (
    <figure
      className="my-10 border border-line bg-white p-6 lg:p-8 overflow-x-auto"
      data-testid="architecture-diagram"
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-widest text-ink-subtle mb-6">
        {title}
      </figcaption>
      <svg
        viewBox="0 0 900 340"
        role="img"
        aria-label="Architecture diagram comparing BLE gateway, cellular/GNSS direct, and checkpoint tracking data paths"
        className="w-full h-auto min-w-[720px]"
      >
        {/* Column headers */}
        <text x="150" y="24" textAnchor="middle" fontSize="12" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">BLE SMART LABEL</text>
        <text x="450" y="24" textAnchor="middle" fontSize="12" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">CELLULAR + GNSS</text>
        <text x="750" y="24" textAnchor="middle" fontSize="12" fill="#0A0A0A" fontFamily="ui-monospace, SFMono-Regular, monospace">CHECKPOINT (RFID / NFC)</text>

        {/* Column separators */}
        <line x1="300" y1="10" x2="300" y2="320" stroke="#EFEFEC" strokeWidth="1" />
        <line x1="600" y1="10" x2="600" y2="320" stroke="#EFEFEC" strokeWidth="1" />

        {/* Column 1 — BLE */}
        <rect x="90" y="52" width="120" height="34" rx="3" fill="#0A0A0A" />
        <text x="150" y="74" textAnchor="middle" fontSize="10" fill="#fff">Label on parcel</text>

        <line x1="150" y1="86" x2="150" y2="108" stroke="#0A0A0A" strokeWidth="1" />
        <text x="158" y="102" fontSize="9" fill="#525252">BLE beacon</text>

        <rect x="90" y="110" width="120" height="34" rx="3" fill="#fff" stroke="#0A0A0A" />
        <text x="150" y="132" textAnchor="middle" fontSize="10" fill="#0A0A0A">Gateway (partner / phone)</text>

        <line x1="150" y1="144" x2="150" y2="164" stroke="#0A0A0A" strokeWidth="1" />
        <text x="158" y="160" fontSize="9" fill="#525252">Internet</text>

        <rect x="90" y="166" width="120" height="34" rx="3" fill="#0047AB" />
        <text x="150" y="188" textAnchor="middle" fontSize="10" fill="#fff">GoAndTrack platform</text>

        <text x="30" y="230" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">STRENGTHS</text>
        <text x="30" y="246" fontSize="10" fill="#0A0A0A">Thin, low-power, low unit cost.</text>
        <text x="30" y="260" fontSize="10" fill="#0A0A0A">Works with existing partner</text>
        <text x="30" y="274" fontSize="10" fill="#0A0A0A">gateway ecosystems.</text>
        <text x="30" y="298" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">TRADE-OFFS</text>
        <text x="30" y="314" fontSize="10" fill="#0A0A0A">Location = gateway visibility.</text>

        {/* Column 2 — Cellular */}
        <rect x="390" y="52" width="120" height="34" rx="3" fill="#0A0A0A" />
        <text x="450" y="74" textAnchor="middle" fontSize="10" fill="#fff">Label on shipment</text>

        <line x1="450" y1="86" x2="450" y2="164" stroke="#0A0A0A" strokeWidth="1" />
        <text x="458" y="130" fontSize="9" fill="#525252">4G / 2G LTE</text>

        <rect x="390" y="166" width="120" height="34" rx="3" fill="#0047AB" />
        <text x="450" y="188" textAnchor="middle" fontSize="10" fill="#fff">GoAndTrack platform</text>

        {/* GNSS side receiver */}
        <text x="450" y="120" textAnchor="middle" fontSize="16" fill="#0047AB">↑</text>
        <text x="450" y="106" textAnchor="middle" fontSize="9" fill="#525252">GNSS satellites</text>

        <text x="330" y="230" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">STRENGTHS</text>
        <text x="330" y="246" fontSize="10" fill="#0A0A0A">Independent of gateway coverage.</text>
        <text x="330" y="260" fontSize="10" fill="#0A0A0A">GPS-grade position, on-board</text>
        <text x="330" y="274" fontSize="10" fill="#0A0A0A">condition sensors, global eSIM.</text>
        <text x="330" y="298" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">TRADE-OFFS</text>
        <text x="330" y="314" fontSize="10" fill="#0A0A0A">Larger form factor, higher unit cost.</text>

        {/* Column 3 — Checkpoint */}
        <rect x="690" y="52" width="120" height="34" rx="3" fill="#0A0A0A" />
        <text x="750" y="74" textAnchor="middle" fontSize="10" fill="#fff">Tag on shipment</text>

        <line x1="750" y1="86" x2="750" y2="108" stroke="#0A0A0A" strokeWidth="1" />
        <text x="758" y="102" fontSize="9" fill="#525252">Scan at node</text>

        <rect x="690" y="110" width="120" height="34" rx="3" fill="#fff" stroke="#0A0A0A" />
        <text x="750" y="132" textAnchor="middle" fontSize="10" fill="#0A0A0A">Fixed reader / handheld</text>

        <line x1="750" y1="144" x2="750" y2="164" stroke="#0A0A0A" strokeWidth="1" />
        <text x="758" y="160" fontSize="9" fill="#525252">Backhaul</text>

        <rect x="690" y="166" width="120" height="34" rx="3" fill="#0047AB" />
        <text x="750" y="188" textAnchor="middle" fontSize="10" fill="#fff">Node-level events</text>

        <text x="630" y="230" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">STRENGTHS</text>
        <text x="630" y="246" fontSize="10" fill="#0A0A0A">Very low unit cost per tag.</text>
        <text x="630" y="260" fontSize="10" fill="#0A0A0A">Zero on-tag battery required.</text>
        <text x="630" y="298" fontSize="9" fill="#525252" fontFamily="ui-monospace, SFMono-Regular, monospace">TRADE-OFFS</text>
        <text x="630" y="314" fontSize="10" fill="#0A0A0A">No inter-node telemetry.</text>
      </svg>
    </figure>
  );
}
